/*
 * Presentational widgets — non-interactive tiles that give the operator
 * tools to structure a dashboard: freetext, headings, images, separators
 * and spacers. None of them touch a data source, so there is no leaf
 * binding, no snapshot, no signal subscription — they're pure config.
 *
 * The widget registry is the single seam through which these become
 * available. Adding a new presentational widget = a new registerWidget()
 * call in this file plus a matching entry in the server's widgets.json
 * (see src/features/Widgets/.../widgets.json). The registry key must
 * match the catalog entry id verbatim.
 */

import type { VNode } from 'preact';
import { registerWidget } from './widgetRegistry';

// ---------------------------------------------------------------------------
// Text — inline-markdown, paragraph-aware, XSS-safe by construction.
// ---------------------------------------------------------------------------

registerWidget({
  id: 'text',
  displayName: 'Text',
  description: 'Freitext / Markdown-Block.',
  chrome: 'bare',
  render: (instance, _ctx) => {
    const content = String(instance.config.content ?? '');
    const align = String(instance.config.align ?? 'left') as 'left' | 'center' | 'right';
    const size = String(instance.config.size ?? 'md') as 'sm' | 'md' | 'lg';
    return (
      <div class={`hv-widget-text hv-widget-text--${size}`} style={{ textAlign: align }}>
        {content
          ? renderMarkdown(content)
          : <span class="muted">Text hier eintragen…</span>}
      </div>
    );
  },
});

// ---------------------------------------------------------------------------
// Heading — section separator with configurable level + optional underline.
// ---------------------------------------------------------------------------

registerWidget({
  id: 'heading',
  displayName: 'Überschrift',
  description: 'Section-Titel mit optionaler Trennlinie.',
  chrome: 'bare',
  render: (instance, _ctx) => {
    const text = String(instance.config.text ?? 'Überschrift');
    const level = (['h2', 'h3', 'h4'] as const).includes(instance.config.level as never)
      ? (instance.config.level as 'h2' | 'h3' | 'h4')
      : 'h3';
    const underline = instance.config.underline !== false;
    return (
      <div class={`hv-widget-heading${underline ? ' hv-widget-heading--underline' : ''} hv-widget-heading--${level}`}>
        {level === 'h2' && <h2 class="hv-widget-heading__text">{text}</h2>}
        {level === 'h3' && <h3 class="hv-widget-heading__text">{text}</h3>}
        {level === 'h4' && <h4 class="hv-widget-heading__text">{text}</h4>}
      </div>
    );
  },
});

// ---------------------------------------------------------------------------
// Image — URL-based, with fit + rounded-corner options.
// ---------------------------------------------------------------------------

registerWidget({
  id: 'image',
  displayName: 'Bild',
  description: 'Statisches Bild aus URL.',
  // Per-instance: when the operator turns "Rahmen anzeigen" off, drop the
  // card chrome so the image bleeds edge-to-edge into the grid cell.
  chrome: (instance) => instance.config.showFrame === false ? 'bare' : 'card',
  render: (instance, _ctx) => {
    const url = String(instance.config.url ?? '');
    const alt = String(instance.config.alt ?? '');
    const fit = (['cover', 'contain', 'fill', 'none'] as const).includes(instance.config.fit as never)
      ? (instance.config.fit as 'cover' | 'contain' | 'fill' | 'none')
      : 'contain';
    const rounded = instance.config.rounded !== false;
    if (!url) {
      return <div class="hv-widget-image hv-widget-image--empty muted">Bild-URL fehlt.</div>;
    }
    // Only accept http(s) URLs — a data: or javascript: URL as image src is
    // not a real threat here (browsers ignore JS in img src), but the
    // constraint keeps the config aligned with the JSON-Schema `format: uri`
    // hint and avoids surprises in shared layouts.
    const safe = /^https?:\/\//i.test(url) ? url : '';
    return (
      <div class={`hv-widget-image${rounded ? ' hv-widget-image--rounded' : ''}`}>
        {safe
          ? <img src={safe} alt={alt} style={{ objectFit: fit }} />
          : <div class="hv-widget-image__error muted">Ungültige URL.</div>}
      </div>
    );
  },
});

// ---------------------------------------------------------------------------
// Separator — one horizontal or vertical divider line.
// ---------------------------------------------------------------------------

registerWidget({
  id: 'separator',
  displayName: 'Trenner',
  description: 'Dünne Trennlinie.',
  chrome: 'bare',
  render: (instance, _ctx) => {
    const orientation = instance.config.orientation === 'vertical' ? 'vertical' : 'horizontal';
    const style = (['solid', 'dashed', 'dotted'] as const).includes(instance.config.style as never)
      ? (instance.config.style as 'solid' | 'dashed' | 'dotted')
      : 'solid';
    return (
      <div
        class={`hv-widget-separator hv-widget-separator--${orientation} hv-widget-separator--${style}`}
        role="separator"
      />
    );
  },
});

// ---------------------------------------------------------------------------
// Spacer — deliberately empty space. Consumes a grid cell without content.
// ---------------------------------------------------------------------------

registerWidget({
  id: 'spacer',
  displayName: 'Abstandshalter',
  description: 'Leere Fläche für Abstand.',
  chrome: 'bare',
  render: (_instance, _ctx) => <div class="hv-widget-spacer" aria-hidden="true" />,
});

// ---------------------------------------------------------------------------
// Minimal, XSS-safe Markdown renderer.
//
// Escapes HTML first, then walks a small subset of markdown grammar. Enough
// for control-room labels, dashboards notes and short instructions; NOT a
// full markdown implementation.
//
// Supported inline:  **bold** *italic* `code` [label](https://url) and \n
// Supported blocks:
//   - `#` .. `####` ATX headings
//   - `-` / `*` bullet lists (contiguous lines)
//   - `1.` ordered lists  (contiguous lines starting with N.)
//   - `>` blockquotes    (contiguous lines)
//   - `| a | b |` GFM-style tables (with optional header separator row)
//   - paragraphs (default)
//
// The output goes into dangerouslySetInnerHTML — this is safe because we
// only ever build markup from constants and from HTML-escaped user text.
// ---------------------------------------------------------------------------

function renderMarkdown(source: string): VNode {
  const escaped = source
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const blocks = escaped.split(/\n{2,}/);
  const parts: VNode[] = [];

  blocks.forEach((block, i) => {
    const trimmed = block.replace(/\s+$/g, '');
    if (!trimmed.trim()) return;
    const lines = trimmed.split('\n');

    // ATX heading (single-line block that starts with 1..4 hashes).
    if (lines.length === 1) {
      const headingMatch = /^(#{1,4})\s+(.+)$/.exec(lines[0].trim());
      if (headingMatch) {
        const level = headingMatch[1].length; // 1..4
        const text = transformInline(headingMatch[2]);
        parts.push(
          <div
            key={i}
            class={`hv-widget-text__h hv-widget-text__h${level}`}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: `<h${level}>${text}</h${level}>` }}
          />,
        );
        return;
      }
    }

    // Bullet list — every line must be a bullet.
    if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
      parts.push(
        <ul key={i} class="hv-widget-text__list">
          {lines.map((l, j) => (
            <li
              key={j}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: transformInline(l.replace(/^\s*[-*]\s+/, '')) }}
            />
          ))}
        </ul>,
      );
      return;
    }

    // Ordered list — every line must start with "N." or "N)".
    if (lines.every((l) => /^\s*\d+[.)]\s+/.test(l))) {
      parts.push(
        <ol key={i} class="hv-widget-text__list hv-widget-text__list--ordered">
          {lines.map((l, j) => (
            <li
              key={j}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: transformInline(l.replace(/^\s*\d+[.)]\s+/, '')) }}
            />
          ))}
        </ol>,
      );
      return;
    }

    // Blockquote — every line starts with ">". (Our HTML-escape has already
    // replaced literal ">" with "&gt;", so we match that.)
    if (lines.every((l) => /^\s*&gt;\s?/.test(l))) {
      const inner = lines
        .map((l) => l.replace(/^\s*&gt;\s?/, ''))
        .join('\n');
      parts.push(
        <blockquote
          key={i}
          class="hv-widget-text__quote"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: transformInline(inner) }}
        />,
      );
      return;
    }

    // GFM table — at least two lines, first is header, optional second is
    // alignment separator "---|---". Cells are pipe-delimited.
    if (lines.length >= 2 && lines.every((l) => l.includes('|'))) {
      const hasSeparator = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(lines[1]);
      const headerCells = splitTableRow(lines[0]);
      const bodyStart = hasSeparator ? 2 : 1;
      const bodyRows = lines.slice(bodyStart).map(splitTableRow);
      if (headerCells.length > 1 && bodyRows.every((r) => r.length > 1)) {
        parts.push(
          <table key={i} class="hv-widget-text__table">
            <thead>
              <tr>
                {headerCells.map((cell, j) => (
                  <th
                    key={j}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: transformInline(cell) }}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, j) => (
                <tr key={j}>
                  {row.map((cell, k) => (
                    <td
                      key={k}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: transformInline(cell) }}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>,
        );
        return;
      }
    }

    // Fallback: paragraph.
    parts.push(
      <p
        key={i}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: transformInline(trimmed) }}
      />,
    );
  });

  return <>{parts}</>;
}

/** Splits one GFM table row into cell strings, tolerating leading/trailing
 *  pipes and trimming whitespace. Escaped pipes (`\|`) are not supported
 *  — control-room tables don't need them and the escape would fight our
 *  HTML-escape pass. */
function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

/** Transforms inline markdown to HTML. Only builds markup from control
 *  patterns — no user HTML flows through (source was pre-escaped). */
function transformInline(s: string): string {
  return s
    // Links: [label](http[s]://url) — reject non-http(s) targets by not matching.
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
    )
    // Bold before italic so **x** doesn't get consumed by the italic rule.
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
    .replace(/`([^`\n]+)`/g, '<code>$1</code>')
    // Soft line-break within a paragraph.
    .replace(/\n/g, '<br>');
}
